/**
 * Within-tab section header rendered above each group of widgets.
 * MER-162: replaces the uniform widget grid with section-bounded
 * sub-grids so a tab reads as L1 -> KPI strip -> L2a -> chart row
 * -> L2b -> table, matching the wrapper mockup + SOTv9.
 *
 * MER-178: the human-readable `label` is now required and the
 * numbered-chip on the left is gone. Section keys like "1.2" or "L2a"
 * are internal authoring conventions and must not appear on the
 * client UI. The `sectionKey` is kept solely for the anchor id so
 * deep links (`#section-L2a`) still work.
 *
 * Title sans-serif 17px 700. Optional caption sub-line for the
 * section description.
 */

import { DOMAINS, domainVar } from "../lib/widgetDomain.mjs";

// MER-584: wash token per domain. Guarded by widget-domain.test.mjs, which
// asserts brand.css defines every one.
const DOMAIN_TOKEN: Record<string, string> = Object.fromEntries(DOMAINS.map((d: string) => [d, d]));

export interface SectionHeaderProps {
  /** Section key (e.g. `L1`, `L2a`, `1.1`). Used only for the anchor id. */
  sectionKey: string;
  /** Human label rendered as the heading. Required (MER-178). */
  label: string;
  /** Optional muted caption (one-liner under the title). */
  caption?: string;
  /** MER-578: domain key, drives the badge swatch + colour. */
  domain?: string;
  /** MER-579: derived provenance, right-aligned. e.g. "KPI Summary - 5 tiles". */
  provenance?: string;
}

export default function SectionHeader(props: SectionHeaderProps) {
  const { sectionKey, label, caption, provenance } = props;
  const domain = props.domain ?? "operations";
  return (
    <header
      id={`section-${sectionKey}`}
      style={{
        gridColumn: "1 / -1",
        display: "grid",
        gap: 2,
        margin: "8px 0 4px",
        minWidth: 0,
      }}
    >
      {/* MER-579: title row. The swatch ties the section to the domain rail on
          every tile beneath it, so the colour reads as a grouping rather than
          decoration, and the right-hand provenance answers "where did this come
          from" without opening anything. */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
        <h2
          style={{
            // MER-584: a chip, per the approved mockup -- 9.5px uppercase mono
            // on the domain's wash, with the swatch inside it. The 16px
            // sentence-case heading it replaces competed with the tile titles
            // for weight; a section marker should recede and let the numbers
            // lead.
            margin: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            lineHeight: 1.3,
            color: domainVar(domain),
            background: `var(--domain-${DOMAIN_TOKEN[domain] ?? "operations"}-wash)`,
            padding: "3px 8px",
            borderRadius: 3,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 7,
              height: 7,
              borderRadius: 1,
              background: domainVar(domain),
              flex: "0 0 auto",
            }}
          />
          {label}
        </h2>
        {provenance ? (
          <span
            style={{
              marginLeft: "auto",
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              color: "var(--text-muted)",
              whiteSpace: "nowrap",
            }}
          >
            {provenance}
          </span>
        ) : null}
      </div>
      {caption ? (
        <p
          style={{
            margin: 0,
            fontSize: 11,
            color: "var(--text-2)",
            lineHeight: 1.3,
          }}
        >
          {caption}
        </p>
      ) : null}
    </header>
  );
}

/**
 * Parse a widget id like `L1.1b.3` / `L2a.1c` / `2.1.2a` / `1.2`
 * into a section key for grouping (MER-178 + MER-189).
 *
 * Rules (in priority order):
 *
 * - L prefix with sub-letter (e.g. `L1b`, `L2a`): the sub-letter is
 *   the section indicator; the second segment is the sibling index
 *   and should NOT add a sub-section split.
 *     "L1b.1"   -> "L1b"
 *     "L1b.10"  -> "L1b"
 *     "L2a.1c"  -> "L2a"
 *     "L2d.4b"  -> "L2d"
 *
 * - L prefix without sub-letter (e.g. `L1`, `L2`): the second segment
 *   carries the sub-section. Strip a trailing letter on the second
 *   segment so sibling cards (a/b/c/d variants) collapse into one
 *   section. If a third segment exists, keep the second segment as
 *   the sub-section (its letter is meaningful, not a sibling index).
 *     "L1.0a"   -> "L1.0"     (Revenue Waterfall variants)
 *     "L1.3"    -> "L1.3"     (single chart, own header)
 *     "L1.4a"   -> "L1.4"     (Unit Economics variants)
 *     "L1.4d"   -> "L1.4"
 *     "L1.1b.1" -> "L1.1b"    (Cash & Collections variants)
 *     "L1.1b.3" -> "L1.1b"
 *
 * - Legacy numeric ids (no "L" prefix): keep the first two segments
 *   so "1.1.3" -> "1.1" but "1.2" stays "1.2".
 *     "2.1.2a"  -> "2.1"
 *     "1.2"     -> "1.2"
 *
 * Untagged widgets (no dot-segment prefix) fall under "Other" so
 * they don't crash the renderer.
 */
const L_FULL = /^(L\d+)([a-z]?)(?:\.(\d+[a-z]?))?(?:\.(\d+[a-z]?))?/;
const NUMERIC_TWO = /^\d+\.\d+/;
const NUMERIC_ONE = /^\d+/;

export function widgetSection(id: string | undefined | null): string {
  if (!id) return "Other";
  const trimmed = id.trim();
  if (!trimmed) return "Other";
  const lm = L_FULL.exec(trimmed);
  if (lm) {
    const lDigits = lm[1]; // "L1" / "L2"
    const lLetter = lm[2] ?? ""; // "b" / "a" / ""
    const second = lm[3]; // "1b" / "4a" / "3" / undefined
    const third = lm[4]; // "1" / undefined
    // MER-199 to MER-210: first-segment-with-letter prefixes
    // (L1b, L2a, L3b, L3d, L6a, L6b, L6c) are treated the same as
    // first-segment-without-letter (L1, L2): always include the
    // second segment, with trailing letter stripped, so siblings
    // L2a.3a/3b/3c collapse into "L2a.3" instead of all sharing
    // one giant "L2a" key. Per-tab grouping into display sections
    // happens via SECTION_MERGES (sectionLabels.ts) which can
    // collapse multiple keys back together for the cases where a
    // visual row spans more than one prefix.
    const firstSegment = `${lDigits}${lLetter}`;
    if (third !== undefined) {
      // 3-segment id (L1.1b.1): second segment is the sub-section.
      return `${firstSegment}.${second}`;
    }
    if (second !== undefined) {
      // 2-segment id (L1.4a, L1.3, L1b.1): strip trailing letter
      // on the second segment so a/b/c/d variants collapse.
      const stripped = second.replace(/[a-z]$/, "");
      return `${firstSegment}.${stripped}`;
    }
    return firstSegment;
  }
  const two = NUMERIC_TWO.exec(trimmed);
  if (two) return two[0];
  const one = NUMERIC_ONE.exec(trimmed);
  if (one) return one[0];
  return "Other";
}
